import { useEffect, useMemo, useState } from 'react';
import { Box, Stack } from '@mantine/core';
import type { Field } from '../../types';
import { useMemoStore } from '../../stores/memoStore';
import { MemoHeader } from './MemoHeader';
import { MemoInputForm } from './MemoInputForm';
import { MemoList } from './MemoList';

interface RightPanelProps {
  fields: Field[];
  farmId: string | null;
}

/**
 * 右サイドバーパネルコンポーネント。
 *
 * 圃場メモの一覧表示と追加機能を提供する。表示対象の圃場に対して
 * メモを一括フェッチし、createdAt 降順で集約表示する。
 */
export function RightPanel({ fields, farmId }: RightPanelProps) {
  const [showInputForm, setShowInputForm] = useState(false);
  const memosByFieldId = useMemoStore((state) => state.memosByFieldId);
  const fetchMemosByFieldIds = useMemoStore((state) => state.fetchMemosByFieldIds);
  const getMemosByFieldIds = useMemoStore((state) => state.getMemosByFieldIds);

  const targetFieldIds = useMemo(() => {
    const ids = farmId
      ? fields.filter((f) => f.farmId === farmId).map((f) => f.id)
      : fields.map((f) => f.id);
    return Array.from(new Set(ids));
  }, [fields, farmId]);

  useEffect(() => {
    if (targetFieldIds.length > 0) {
      void fetchMemosByFieldIds(targetFieldIds);
    }
  }, [targetFieldIds, fetchMemosByFieldIds]);

  // memosByFieldId を購読して、メモ追加時にも再計算されるようにする
  const memos = useMemo(
    () => getMemosByFieldIds(targetFieldIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [targetFieldIds, memosByFieldId]
  );

  const handleToggleInput = () => {
    setShowInputForm((prev) => !prev);
  };

  const handleSubmit = () => {
    setShowInputForm(false);
  };

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack gap="sm">
        <MemoHeader onAddClick={handleToggleInput} />
        {showInputForm && targetFieldIds.length > 0 && (
          <MemoInputForm fields={fields} farmId={farmId} onSubmit={handleSubmit} />
        )}
        <MemoList memos={memos} fields={fields} />
      </Stack>
    </Box>
  );
}
